'use client'
import {
  CarouselTitle_default,
  CarouselWithLink_default
} from "./chunk-LVDZESI2.mjs";
import {
  ContentfulRichText_default
} from "./chunk-DR2RGIIC.mjs";
import {
  DecoratedSection_default
} from "./chunk-HJCFOVNG.mjs";
import {
  ContentfulButton_default
} from "./chunk-K3ITZBKW.mjs";
import {
  ContentfulMedia_default
} from "./chunk-BYRNGPWN.mjs";
import {
  useSectionTagFilter
} from "./chunk-ISPH2TSR.mjs";
import {
  parseSectionItem
} from "./chunk-DJNQ4XY4.mjs";
import {
  injectHtmlSection
} from "./chunk-4FXSFWXZ.mjs";
import {
  useGetSectionHtmlContent
} from "./chunk-E42MQMXB.mjs";
import {
  useSections
} from "./chunk-VSTAKFRX.mjs";
import {
  useCards
} from "./chunk-5DDVDLZW.mjs";
import {
  __spreadProps,
  __spreadValues
} from "./chunk-UYSR6ZT4.mjs";

// src/components/section/FeaturesGrid.tsx
import { Divider, Typography } from "@wbk/ui";
import { twMerge } from "tailwind-merge";
import { jsx, jsxs } from "react/jsx-runtime";
var FeaturesGrid = ({ section, className }) => {
  var _a;
  const cards = (_a = section.contentCollection) == null ? void 0 : _a.items;
  if (!cards.length) return null;
  return /* @__PURE__ */ jsx(
    DecoratedSection_default,
    {
      section,
      className: twMerge("container", className),
      "data-testid": `section_features_grid_${section.id}`,
      children: /* @__PURE__ */ jsxs("div", { className: "space-y-8", children: [
        /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
          /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
            /* @__PURE__ */ jsx(Typography, { variant: "heading-S", color: "primary", children: section.subtitle }),
            /* @__PURE__ */ jsx(Typography, { variant: "display-S", children: section.title })
          ] }),
          !!section.collapsableDescription && /* @__PURE__ */ jsx("div", { className: "max-w-2xl", children: /* @__PURE__ */ jsx(ContentfulRichText_default, { description: section.collapsableDescription }) })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4", children: cards.map((card) => /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
          /* @__PURE__ */ jsx(ContentfulMedia_default, { media: card.icon, className: "h-12 w-12 shrink-0" }),
          /* @__PURE__ */ jsx(Divider, { type: "zigzag", className: "text-text-tertiary" }),
          /* @__PURE__ */ jsx(Typography, { variant: "heading-S", children: card.title }),
          !!card.description && /* @__PURE__ */ jsx(
            Typography,
            {
              variant: "label-L",
              dangerouslySetInnerHTML: { __html: card.description }
            }
          )
        ] }, card.id)) })
      ] })
    }
  );
};
var FeaturesGrid_default = FeaturesGrid;

// src/components/section/HtmlSection.tsx
import { useEffect, useRef } from "react";
import { AnimatedWbkLogo } from "@wbk/svg";
import { useAppLoading, useRouter } from "@wbk/config";
import { useCurrency } from "@wbk/currency";
import { jsx as jsx2 } from "react/jsx-runtime";
var HtmlSection = ({ section }) => {
  var _a;
  const url = (_a = section.html) == null ? void 0 : _a.url;
  const containerRef = useRef(null);
  const { locale } = useRouter();
  const { currency } = useCurrency();
  const { appLoadingComponent } = useAppLoading();
  const { data: html, isLoading, error } = useGetSectionHtmlContent(url);
  useEffect(() => {
    const container = containerRef.current;
    if (!html || !container) return;
    return injectHtmlSection(container, html, {
      mode: "consumer-host",
      executeScripts: true
    });
  }, [html, locale, currency.shortCode]);
  if (!url) return null;
  if (isLoading) {
    return /* @__PURE__ */ jsx2("div", { className: "flex h-[calc(100svh-200px)] w-full items-center justify-center", children: appLoadingComponent ? appLoadingComponent : /* @__PURE__ */ jsx2(AnimatedWbkLogo, { className: "h-20 w-52 md:h-28 md:py-4" }) });
  }
  if (error || !html) return null;
  return /* @__PURE__ */ jsx2("div", { ref: containerRef, "data-testid": `section_html_${section.id}` });
};
var HtmlSection_default = HtmlSection;

// src/components/section/IconsGrid.tsx
import { Button, Typography as Typography2 } from "@wbk/ui";
import { Link, useRouter as useRouter2, useTranslation } from "@wbk/config";
import { useAnalytics } from "@wbk/analytics";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var IconsGrid = ({ section }) => {
  const { locale } = useRouter2();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation("contentful_common");
  const items = section.contentCollection.items;
  if (!(items == null ? void 0 : items.length)) return null;
  return /* @__PURE__ */ jsxs2(
    DecoratedSection_default,
    {
      section,
      "data-testid": `icons_grid_${section.id}`,
      className: "container",
      children: [
        section.title && /* @__PURE__ */ jsx3(Typography2, { as: "h2", variant: "heading-L", children: section.title }),
        section.subtitle && /* @__PURE__ */ jsx3(Typography2, { color: "text-secondary", className: "mt-1", children: section.subtitle }),
        /* @__PURE__ */ jsx3(
          "div",
          {
            "data-start": items.length > 12,
            className: "gap-S flex flex-wrap items-start lg:justify-center lg:data-[start=true]:justify-start",
            children: items.map((item, idx) => {
              var _a;
              const parsed = parseSectionItem({ item, locale });
              if (!parsed) return null;
              const isLink = !!parsed.href && parsed.href !== "#";
              const isExternal = isLink && ((_a = parsed.href) == null ? void 0 : _a.startsWith("http"));
              return /* @__PURE__ */ jsxs2(
                Button,
                {
                  as: isLink ? Link : "div",
                  href: parsed.href || "#",
                  theme: "action",
                  shape: "text",
                  className: "basis-[calc(25%-var(--spacing-S))] flex-col gap-0 p-1 md:max-w-20",
                  target: isExternal ? "_blank" : void 0,
                  onClick: () => {
                    var _a2;
                    trackEvent({
                      event: "clicked_on_section_item",
                      data: {
                        type: item.__typename,
                        item_id: (_a2 = item.sys) == null ? void 0 : _a2.id,
                        slug: "slug" in item ? item.slug : parsed.href,
                        label: parsed.title,
                        section: section.id,
                        position: idx
                      }
                    });
                  },
                  rel: "noreferrer",
                  children: [
                    /* @__PURE__ */ jsx3(
                      ContentfulMedia_default,
                      {
                        className: "mx-auto size-10 object-contain",
                        media: parsed.icon || parsed.background || parsed.image11
                      }
                    ),
                    /* @__PURE__ */ jsx3(Typography2, { variant: "label-XS", className: "mt-1 line-clamp-1", children: parsed.title }),
                    parsed.isNew && /* @__PURE__ */ jsx3(Typography2, { variant: "label-XS", className: "line-clamp-1 leading-3", color: "primary", children: t("contentful_common:new") })
                  ]
                },
                idx
              );
            })
          }
        )
      ]
    }
  );
};
var IconsGrid_default = IconsGrid;

// src/components/section/IconsLargeGrid.tsx
import { Button as Button2, Typography as Typography3 } from "@wbk/ui";
import { Link as Link2, useRouter as useRouter3 } from "@wbk/config";
import { useAnalytics as useAnalytics2 } from "@wbk/analytics";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var IconsLargeGrid = ({ section }) => {
  const { locale } = useRouter3();
  const { trackEvent } = useAnalytics2();
  const items = section.contentCollection.items;
  if (!(items == null ? void 0 : items.length)) return null;
  return /* @__PURE__ */ jsxs3(
    DecoratedSection_default,
    {
      section,
      "data-testid": `icons_large_grid_${section.id}`,
      className: "container",
      children: [
        section.title && /* @__PURE__ */ jsx4(Typography3, { as: "h2", variant: "heading-L", children: section.title }),
        section.subtitle && /* @__PURE__ */ jsx4(Typography3, { color: "text-secondary", className: "mt-1", children: section.subtitle }),
        /* @__PURE__ */ jsx4("div", { className: "gap-S grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8", children: items.map((item, idx) => {
          var _a;
          const parsed = parseSectionItem({ item, locale });
          if (!parsed) return null;
          const isLink = !!parsed.href && parsed.href !== "#";
          const isExternal = isLink && ((_a = parsed.href) == null ? void 0 : _a.startsWith("http"));
          return /* @__PURE__ */ jsxs3(
            Button2,
            {
              as: isLink ? Link2 : "div",
              href: parsed.href ? parsed.href : "#",
              theme: "action",
              shape: "text",
              className: "flex-col justify-start gap-0",
              target: isExternal ? "_blank" : void 0,
              onClick: () => {
                var _a2;
                trackEvent({
                  event: "clicked_on_section_item",
                  data: {
                    type: item.__typename,
                    item_id: (_a2 = item.sys) == null ? void 0 : _a2.id,
                    slug: "slug" in item ? item.slug : parsed.href,
                    section: section.id,
                    position: idx
                  }
                });
              },
              rel: "noreferrer",
              children: [
                /* @__PURE__ */ jsx4(
                  ContentfulMedia_default,
                  {
                    className: "aspect-square w-full object-cover md:max-w-36",
                    media: parsed.icon || parsed.background || parsed.image11
                  }
                ),
                /* @__PURE__ */ jsx4(Typography3, { variant: "label-M", className: "mt-1", children: parsed.title }),
                /* @__PURE__ */ jsx4(Typography3, { variant: "body-XS", color: "text-secondary", children: parsed.subtitle })
              ]
            },
            idx
          );
        }) })
      ]
    }
  );
};
var IconsLargeGrid_default = IconsLargeGrid;

// src/components/section/InfoCardsGrid.tsx
import { Paper, Typography as Typography4 } from "@wbk/ui";
import { twMerge as twMerge2 } from "tailwind-merge";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var InfoCardsGrid = ({ section, className }) => {
  var _a;
  const cards = (_a = section.contentCollection) == null ? void 0 : _a.items;
  if (!cards.length) return null;
  return /* @__PURE__ */ jsx5(
    DecoratedSection_default,
    {
      section,
      className: twMerge2("container", className),
      "data-testid": `section_info_cards_grid_${section.id}`,
      children: /* @__PURE__ */ jsxs4("div", { className: "space-y-8", children: [
        /* @__PURE__ */ jsxs4("div", { className: "space-y-6", children: [
          /* @__PURE__ */ jsxs4("div", { className: "space-y-2", children: [
            /* @__PURE__ */ jsx5(Typography4, { variant: "heading-S", color: "primary", children: section.subtitle }),
            /* @__PURE__ */ jsx5(Typography4, { variant: "display-S", children: section.title })
          ] }),
          !!section.collapsableDescription && /* @__PURE__ */ jsx5("div", { className: "max-w-2xl", children: /* @__PURE__ */ jsx5(ContentfulRichText_default, { description: section.collapsableDescription }) })
        ] }),
        /* @__PURE__ */ jsx5("div", { className: "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3", children: cards.map((card) => /* @__PURE__ */ jsxs4(Paper, { className: "space-y-6", surface: "level-1", size: "L", children: [
          /* @__PURE__ */ jsx5(ContentfulMedia_default, { media: card.icon, className: "h-12 w-12 shrink-0" }),
          /* @__PURE__ */ jsxs4("div", { className: "space-y-2", children: [
            /* @__PURE__ */ jsx5(Typography4, { variant: "body-L", strong: true, color: "text-secondary", children: card.title }),
            /* @__PURE__ */ jsx5(Typography4, { variant: "heading-S", children: card.subtitle }),
            !!card.description && /* @__PURE__ */ jsx5(
              Typography4,
              {
                variant: "label-L",
                color: "text-secondary",
                dangerouslySetInnerHTML: { __html: card.description }
              }
            )
          ] })
        ] }, card.id)) })
      ] })
    }
  );
};
var InfoCardsGrid_default = InfoCardsGrid;

// src/components/section/InfoModal.tsx
import { Fragment, useMemo } from "react";
import { twMerge as twMerge3 } from "tailwind-merge";
import { Button as Button3, Paper as Paper2, ResponsiveModal, Typography as Typography5 } from "@wbk/ui";
import { useTranslation as useTranslation2 } from "@wbk/config";
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
var InfoModal = ({ section }) => {
  const { t } = useTranslation2("contentful_common");
  const tables = useMemo(() => {
    var _a;
    const webCards = [];
    for (const item of section.contentCollection.items) {
      if (item.__typename === "WebCard") {
        if (item.id.includes("table-header")) {
          webCards.push({
            header: item,
            columns: [],
            rowCount: 0
          });
        } else if (webCards.length) {
          const table = webCards[webCards.length - 1];
          const cells = ((_a = item.cmsTagsCollection) == null ? void 0 : _a.items) || [];
          table.columns.push({
            id: item.sys.id,
            title: item.title,
            subtitle: item.subtitle,
            cells
          });
          table.rowCount = Math.max(table.rowCount, cells.length);
        }
      }
    }
    return webCards;
  }, [section.contentCollection.items]);
  return /* @__PURE__ */ jsx6(
    DecoratedSection_default,
    {
      section,
      className: "container",
      "data-testid": `section_info_modal_${section.id}`,
      children: /* @__PURE__ */ jsx6(
        ResponsiveModal,
        {
          title: section.title,
          fullscreen: true,
          customAction: ({ close }) => /* @__PURE__ */ jsxs5("div", { className: "flex w-full items-center justify-between gap-4", children: [
            section.collapsableDescription && /* @__PURE__ */ jsx6(ContentfulRichText_default, { description: section.collapsableDescription }),
            /* @__PURE__ */ jsx6(Button3, { onClick: close, theme: "action", children: t("contentful_common:done") })
          ] }),
          render: ({ open }) => {
            var _a, _b;
            return /* @__PURE__ */ jsxs5(Paper2, { surface: "level-1", border: false, className: "flex items-center justify-between", children: [
              /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-3", children: [
                section.logo && /* @__PURE__ */ jsx6(
                  ContentfulMedia_default,
                  {
                    media: section.logo,
                    className: "h-9 w-9 shrink-0",
                    format: null
                  }
                ),
                /* @__PURE__ */ jsxs5("div", { children: [
                  /* @__PURE__ */ jsx6(Typography5, { variant: "label-M", children: section.title }),
                  /* @__PURE__ */ jsx6(Typography5, { variant: "body-S", children: section.subtitle })
                ] })
              ] }),
              !!((_b = (_a = section.webLinksCollection) == null ? void 0 : _a.items[0]) == null ? void 0 : _b.label) && !!tables.length && /* @__PURE__ */ jsx6(Button3, { onClick: open, theme: "transparent", className: "shrink-0", children: /* @__PURE__ */ jsx6(Typography5, { variant: "link-S", children: section.webLinksCollection.items[0].label }) })
            ] });
          },
          body: () => /* @__PURE__ */ jsx6("div", { className: "space-y-4", children: tables.map((table) => /* @__PURE__ */ jsxs5(Fragment, { children: [
            (table.header.title || table.header.subtitle) && /* @__PURE__ */ jsxs5("div", { className: "space-y-1", children: [
              table.header.title && /* @__PURE__ */ jsx6(Typography5, { variant: "heading-S", children: table.header.title }),
              table.header.subtitle && /* @__PURE__ */ jsx6(Typography5, { variant: "body-S", children: table.header.subtitle })
            ] }),
            !!table.columns.length && /* @__PURE__ */ jsx6("div", { className: "overflow-x-auto", children: /* @__PURE__ */ jsxs5(
              "div",
              {
                className: "min-w-[42rem] overflow-hidden rounded-md",
                style: {
                  display: "grid",
                  gridTemplateColumns: `repeat(${table.columns.length}, minmax(9rem, 1fr))`
                },
                children: [
                  table.columns.map((column, index) => /* @__PURE__ */ jsx6(
                    "div",
                    {
                      className: "bg-text/10 p-4",
                      children: /* @__PURE__ */ jsxs5("div", { className: "space-y-1", children: [
                        /* @__PURE__ */ jsx6(Typography5, { variant: "label-S", children: column.title }),
                        column.subtitle && /* @__PURE__ */ jsx6(Typography5, { variant: "body-XS", color: "text-secondary", children: column.subtitle })
                      ] })
                    },
                    column.id || `${table.header.sys.id}-header-${index}`
                  )),
                  Array.from({ length: table.rowCount }).map((_, rowIndex) => {
                    const isLastRow = rowIndex === table.rowCount - 1;
                    return table.columns.map((column, columnIndex) => {
                      const cell = column.cells[rowIndex];
                      return /* @__PURE__ */ jsx6(
                        "div",
                        {
                          className: twMerge3(
                            "border-text-tertiary/5 border-t p-4",
                            rowIndex % 2 === 0 ? "bg-body-dark/5" : "bg-body-level-2/5",
                            isLastRow && "pb-4"
                          ),
                          children: (cell == null ? void 0 : cell.title) ? /* @__PURE__ */ jsxs5("div", { className: "space-y-1", children: [
                            /* @__PURE__ */ jsx6(Typography5, { variant: "body-S", children: cell.title }),
                            cell.subtitle && /* @__PURE__ */ jsx6(Typography5, { variant: "body-XS", color: "text-secondary", children: cell.subtitle })
                          ] }) : /* @__PURE__ */ jsx6(Typography5, { variant: "body-S", color: "text-tertiary", children: "-" })
                        },
                        `${table.header.sys.id}-${rowIndex}-${column.id || columnIndex}`
                      );
                    });
                  })
                ]
              }
            ) })
          ] }, table.header.sys.id)) })
        }
      )
    }
  );
};
var InfoModal_default = InfoModal;

// src/components/section/InfoModalAsync.tsx
import { useRouter as useRouter4 } from "@wbk/config";
import { jsx as jsx7 } from "react/jsx-runtime";
var InfoModalAsync = ({ id, countryCode }) => {
  var _a;
  const { locale } = useRouter4();
  const sectionId = countryCode ? `info_modal_${countryCode}` : id || "info_modal_SA";
  const { data } = useSections({
    limit: 1,
    lang: locale,
    where: { id: sectionId }
  });
  const displaySection = (_a = data == null ? void 0 : data.items) == null ? void 0 : _a[0];
  if (!displaySection) return null;
  return /* @__PURE__ */ jsx7(InfoModal_default, { section: displaySection });
};
var InfoModalAsync_default = InfoModalAsync;

// src/components/section/RecapSection.tsx
import { jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
var RecapSection = ({ section }) => {
  const card = section.contentCollection.items[0];
  if (!card) return null;
  return /* @__PURE__ */ jsxs6(DecoratedSection_default, { section, className: "container relative", children: [
    /* @__PURE__ */ jsx8(
      "svg",
      {
        width: "812",
        height: "705",
        viewBox: "0 0 812 705",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        className: "absolute top-0 -z-10 max-h-full max-w-full ltr:-left-12 rtl:-right-12 rtl:-rotate-180 rtl:-scale-y-100",
        children: /* @__PURE__ */ jsx8(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M811.5 705H596.5H401.5L309.092 648.995L-36 449.991C81.9193 271.136 235.461 117.778 414.53 0L811.5 705Z",
            fill: "#987BFF"
          }
        )
      }
    ),
    /* @__PURE__ */ jsx8("div", { className: "container", children: /* @__PURE__ */ jsxs6("div", { className: "grid items-center gap-6 md:grid-cols-2", children: [
      /* @__PURE__ */ jsxs6("div", { className: "space-y-4", children: [
        card.icon && /* @__PURE__ */ jsx8(
          ContentfulMedia_default,
          {
            media: card.icon,
            className: "object-contain",
            width: 70,
            height: 70,
            alt: card.title
          }
        ),
        /* @__PURE__ */ jsx8("h3", { className: "my-4 text-white", children: card.title }),
        /* @__PURE__ */ jsx8("div", { className: "flex gap-4", children: section.webLinksCollection.items.map((link) => /* @__PURE__ */ jsx8(ContentfulButton_default, { link }, link.id)) })
      ] }),
      card.background && /* @__PURE__ */ jsx8(
        ContentfulMedia_default,
        {
          className: "aspect-[16/9]",
          poster: card.image.url,
          media: card.background,
          height: 400,
          width: 800,
          placeholder: true,
          controls: true
        }
      )
    ] }) })
  ] });
};
var RecapSection_default = RecapSection;

// src/components/section/RectangularCarousel.tsx
import { twMerge as twMerge4 } from "tailwind-merge";
import { Button as Button5, Typography as Typography7 } from "@wbk/ui";
import { Link as Link3 } from "@wbk/config";
import { Fragment as Fragment2 } from "react/jsx-runtime";
import { useAnalytics as useAnalytics3 } from "@wbk/analytics";

// src/components/filters/SectionTagFilter.tsx
import { Button as Button4, Carousel, Typography as Typography6 } from "@wbk/ui";
import { jsx as jsx9 } from "react/jsx-runtime";
var SectionTagFilter = ({ tags, activeTag, onSelect }) => {
  if (!(tags == null ? void 0 : tags.length)) return null;
  return /* @__PURE__ */ jsx9(Carousel, { children: tags.map((tag) => /* @__PURE__ */ jsx9(
    Button4,
    {
      shape: "outlined",
      theme: "action",
      "data-active": (activeTag == null ? void 0 : activeTag.id) === tag.id,
      onClick: () => onSelect(tag.id === (activeTag == null ? void 0 : activeTag.id) ? void 0 : tag),
      className: "ltr:mr-S rtl:ml-S data-[active=false]:border-action/20 data-[active=false]:text-action/50 shrink-0",
      children: /* @__PURE__ */ jsx9(Typography6, { variant: "label-M", children: tag.title })
    },
    tag.id
  )) });
};
var SectionTagFilter_default = SectionTagFilter;

// src/components/section/RectangularCarousel.tsx
import { jsx as jsx10, jsxs as jsxs7 } from "react/jsx-runtime";
var RectangularCarousel = ({ section }) => {
  var _a, _b;
  const items = section.contentCollection.items;
  const tags = section.tagsCollection.items;
  const isNonOverlay = section.renderType === "rectangular-carousel-non-overlay";
  const { trackEvent } = useAnalytics3();
  const { tag, setTag, filteredItems } = useSectionTagFilter({
    items,
    tags
  });
  if (!(items == null ? void 0 : items.length)) return null;
  return /* @__PURE__ */ jsx10(
    DecoratedSection_default,
    {
      section,
      "data-testid": `section_rectangular_carousel_${section.id}`,
      className: "container",
      children: /* @__PURE__ */ jsx10(
        CarouselWithLink_default,
        {
          section,
          linkClassName: "relative h-full shrink-0 basis-56 md:basis-60 rounded-md aspect-[2/3]",
          arrowsClassName: "[&>div:first-child]:min-w-0",
          title: (tags == null ? void 0 : tags.length) ? /* @__PURE__ */ jsxs7("div", { className: "space-y-4", children: [
            /* @__PURE__ */ jsx10(
              CarouselTitle_default,
              {
                section,
                sectionLink: (_b = (_a = section.webLinksCollection) == null ? void 0 : _a.items) == null ? void 0 : _b[0]
              }
            ),
            /* @__PURE__ */ jsx10(SectionTagFilter_default, { tags, activeTag: tag, onSelect: setTag })
          ] }) : void 0,
          children: filteredItems.map((item, i) => {
            var _a2;
            const isLink = !!item.href && item.href !== "#";
            const isExternal = isLink && ((_a2 = item.href) == null ? void 0 : _a2.startsWith("http"));
            return /* @__PURE__ */ jsx10(Fragment2, { children: /* @__PURE__ */ jsxs7(
              "div",
              {
                className: "relative h-full shrink-0 basis-56 rounded-md md:basis-60 ltr:mr-4 rtl:ml-4",
                children: [
                  /* @__PURE__ */ jsxs7(
                    Button5,
                    {
                      as: isLink ? Link3 : "div",
                      href: item.href || "#",
                      className: twMerge4(
                        "relative flex aspect-[2/3] w-full rounded-md p-0 text-start",
                        !isNonOverlay && "before:via-body/10 before:to-body before:absolute before:z-10 before:h-full before:w-full before:bg-gradient-to-b before:from-transparent"
                      ),
                      theme: "action",
                      shape: "text",
                      target: isExternal ? "_blank" : void 0,
                      rel: "noreferrer",
                      "data-testid": `section_rectangular_carousel_item_${item.id}`,
                      onClick: () => {
                        var _a3;
                        trackEvent({
                          event: "clicked_on_section_item",
                          data: {
                            type: item.__typename,
                            item_id: (_a3 = item.sys) == null ? void 0 : _a3.id,
                            slug: "slug" in item ? item.slug : "",
                            section: section.id,
                            position: i
                          }
                        });
                      },
                      children: [
                        /* @__PURE__ */ jsx10(
                          ContentfulMedia_default,
                          {
                            className: "aspect-1 absolute h-full overflow-hidden rounded-lg object-cover",
                            media: item.background || item.image11,
                            width: 600,
                            height: 900,
                            fit: "thumb",
                            ratio: "1x1",
                            alt: item.title,
                            placeholder: true
                          }
                        ),
                        !isNonOverlay && /* @__PURE__ */ jsxs7("div", { className: "relative z-50 flex h-full w-full items-end justify-between gap-2 overflow-hidden p-4", children: [
                          /* @__PURE__ */ jsxs7("div", { className: "space-y-1", children: [
                            /* @__PURE__ */ jsx10(Typography7, { variant: "heading-XS", className: "line-clamp-2", children: item.title }),
                            item.subtitle && /* @__PURE__ */ jsx10(Typography7, { variant: "body-S", children: item.subtitle })
                          ] }),
                          item.icon && /* @__PURE__ */ jsx10(
                            ContentfulMedia_default,
                            {
                              className: "shrink-0",
                              media: item.icon,
                              width: 30,
                              height: 30,
                              fit: "pad"
                            }
                          )
                        ] })
                      ]
                    }
                  ),
                  isNonOverlay && /* @__PURE__ */ jsxs7("div", { className: "py-M flex h-full w-full items-end justify-between gap-2 overflow-hidden", children: [
                    /* @__PURE__ */ jsxs7("div", { className: "space-y-1", children: [
                      /* @__PURE__ */ jsx10(Typography7, { variant: "heading-XS", as: "h3", className: "line-clamp-2", children: item.title }),
                      item.subtitle && /* @__PURE__ */ jsx10(Typography7, { variant: "body-S", children: item.subtitle })
                    ] }),
                    item.icon && /* @__PURE__ */ jsx10(
                      ContentfulMedia_default,
                      {
                        className: "shrink-0",
                        media: item.icon,
                        width: 30,
                        height: 30,
                        fit: "pad"
                      }
                    )
                  ] })
                ]
              },
              i
            ) }, i);
          })
        }
      )
    }
  );
};
var RectangularCarousel_default = RectangularCarousel;

// src/components/section/RectangularCardCarousel.tsx
import { twMerge as twMerge5 } from "tailwind-merge";
import { Button as Button6, Typography as Typography8 } from "@wbk/ui";
import { Link as Link4 } from "@wbk/config";
import { Fragment as Fragment3 } from "react/jsx-runtime";
import { useAnalytics as useAnalytics4 } from "@wbk/analytics";
import { jsx as jsx11, jsxs as jsxs8 } from "react/jsx-runtime";
var RectangularCardCarousel = ({ section }) => {
  var _a, _b;
  const items = section.contentCollection.items;
  const tags = section.tagsCollection.items;
  const tagSlugs = (tags == null ? void 0 : tags.map((tag2) => tag2.slug)) || [];
  const isNonOverlay = section.renderType === "rectangular-card-carousel-infinite-non-overlay";
  const { trackEvent } = useAnalytics4();
  const { data: cards } = useCards(
    {
      where: {
        cmsTags: {
          slug_in: tagSlugs
        }
      }
    },
    {
      enabled: tagSlugs.length > 0
    }
  );
  const cardItems = (cards == null ? void 0 : cards.items) ? cards.items.map((item) => __spreadProps(__spreadValues({}, item), { __typename: "WebCard" })) : items;
  const { tag, setTag, filteredItems } = useSectionTagFilter({
    items: cardItems,
    tags
  });
  if (!(cardItems == null ? void 0 : cardItems.length)) return null;
  return /* @__PURE__ */ jsx11(
    DecoratedSection_default,
    {
      section,
      "data-testid": `section_rectangular_card_carousel_${section.id}`,
      className: "container",
      children: /* @__PURE__ */ jsx11(
        CarouselWithLink_default,
        {
          section,
          linkClassName: "relative h-full shrink-0 basis-56 md:basis-60 rounded-md aspect-[2/3]",
          arrowsClassName: "[&>div:first-child]:min-w-0",
          title: (tags == null ? void 0 : tags.length) ? /* @__PURE__ */ jsxs8("div", { className: "space-y-4", children: [
            /* @__PURE__ */ jsx11(
              CarouselTitle_default,
              {
                section,
                sectionLink: (_b = (_a = section.webLinksCollection) == null ? void 0 : _a.items) == null ? void 0 : _b[0]
              }
            ),
            /* @__PURE__ */ jsx11(SectionTagFilter_default, { tags, activeTag: tag, onSelect: setTag })
          ] }) : void 0,
          children: filteredItems.map((item, i) => {
            var _a2;
            const isLink = !!item.href && item.href !== "#";
            const isExternal = isLink && ((_a2 = item.href) == null ? void 0 : _a2.startsWith("http"));
            return /* @__PURE__ */ jsx11(Fragment3, { children: /* @__PURE__ */ jsxs8(
              "div",
              {
                className: "relative h-full shrink-0 basis-56 rounded-md md:basis-60 ltr:mr-4 rtl:ml-4",
                children: [
                  /* @__PURE__ */ jsxs8(
                    Button6,
                    {
                      as: isLink ? Link4 : "div",
                      href: item.href || "#",
                      className: twMerge5(
                        "relative flex aspect-[2/3] w-full rounded-md p-0 text-start",
                        !isNonOverlay && "before:via-body/10 before:to-body before:absolute before:z-10 before:h-full before:w-full before:bg-gradient-to-b before:from-transparent"
                      ),
                      theme: "action",
                      shape: "text",
                      target: isExternal ? "_blank" : void 0,
                      rel: "noreferrer",
                      "data-testid": `section_rectangular_card_carousel_item_${item.id}`,
                      onClick: () => {
                        var _a3;
                        trackEvent({
                          event: "clicked_on_section_item",
                          data: {
                            type: item.__typename,
                            item_id: (_a3 = item.sys) == null ? void 0 : _a3.id,
                            slug: "slug" in item ? item.slug : "",
                            section: section.id,
                            position: i
                          }
                        });
                      },
                      children: [
                        /* @__PURE__ */ jsx11(
                          ContentfulMedia_default,
                          {
                            className: "aspect-1 absolute h-full overflow-hidden rounded-lg object-cover",
                            media: item.background || item.image11,
                            width: 600,
                            height: 900,
                            fit: "thumb",
                            ratio: "1x1",
                            alt: item.title,
                            placeholder: true
                          }
                        ),
                        !isNonOverlay && /* @__PURE__ */ jsxs8("div", { className: "relative z-50 flex h-full w-full items-end justify-between gap-2 overflow-hidden p-4", children: [
                          /* @__PURE__ */ jsxs8("div", { className: "space-y-1", children: [
                            /* @__PURE__ */ jsx11(Typography8, { variant: "heading-XS", className: "line-clamp-2", children: item.title }),
                            item.subtitle && /* @__PURE__ */ jsx11(Typography8, { variant: "body-S", children: item.subtitle })
                          ] }),
                          item.icon && /* @__PURE__ */ jsx11(
                            ContentfulMedia_default,
                            {
                              className: "shrink-0",
                              media: item.icon,
                              width: 30,
                              height: 30,
                              fit: "pad"
                            }
                          )
                        ] })
                      ]
                    }
                  ),
                  isNonOverlay && /* @__PURE__ */ jsxs8("div", { className: "py-M flex h-full w-full items-end justify-between gap-2 overflow-hidden", children: [
                    /* @__PURE__ */ jsxs8("div", { className: "space-y-1", children: [
                      /* @__PURE__ */ jsx11(Typography8, { variant: "heading-XS", as: "h3", className: "line-clamp-2", children: item.title }),
                      item.subtitle && /* @__PURE__ */ jsx11(Typography8, { variant: "body-S", children: item.subtitle })
                    ] }),
                    item.icon && /* @__PURE__ */ jsx11(
                      ContentfulMedia_default,
                      {
                        className: "shrink-0",
                        media: item.icon,
                        width: 30,
                        height: 30,
                        fit: "pad"
                      }
                    )
                  ] })
                ]
              },
              i
            ) }, i);
          })
        }
      )
    }
  );
};
var RectangularCardCarousel_default = RectangularCardCarousel;

// src/components/section/ShowcaseCarousel.tsx
import { Carousel as Carousel2, Typography as Typography9 } from "@wbk/ui";
import { Fragment as Fragment4, jsx as jsx12, jsxs as jsxs9 } from "react/jsx-runtime";
var ShowcaseCarousel = ({ section }) => {
  var _a;
  const cards = (_a = section == null ? void 0 : section.contentCollection) == null ? void 0 : _a.items;
  if (!(cards == null ? void 0 : cards.length)) return null;
  return /* @__PURE__ */ jsx12(
    DecoratedSection_default,
    {
      section,
      "data-testid": `section_cards_carousel_${section == null ? void 0 : section.id}`,
      id: section == null ? void 0 : section.id,
      children: /* @__PURE__ */ jsx12(
        Carousel2,
        {
          options: {
            loop: cards.length > 4,
            dragFree: true,
            duration: 4e4
          },
          autoplay: cards.length > 4 ? { delay: 0 } : false,
          arrowsClassName: "container",
          title: /* @__PURE__ */ jsxs9(Fragment4, { children: [
            (section == null ? void 0 : section.title) && /* @__PURE__ */ jsx12(Typography9, { as: "h2", variant: "heading-L", children: section == null ? void 0 : section.title }),
            (section == null ? void 0 : section.subtitle) && /* @__PURE__ */ jsx12(Typography9, { color: "text-secondary", className: "mt-1", children: section == null ? void 0 : section.subtitle })
          ] }),
          children: cards == null ? void 0 : cards.map((item) => /* @__PURE__ */ jsx12(
            ContentfulMedia_default,
            {
              media: item == null ? void 0 : item.image,
              className: "aspect-[3/2] shrink-0 basis-[75%] overflow-hidden rounded-md object-cover sm:basis-[60%] md:basis-[50%] lg:basis-[40%] xl:basis-[30%] ltr:mr-2 rtl:ml-2",
              placeholder: true,
              ratio: "3x1"
            },
            item.id
          ))
        }
      )
    }
  );
};
var ShowcaseCarousel_default = ShowcaseCarousel;

// src/components/section/StepsTimeline.tsx
import { Fragment as Fragment5 } from "react";
import { Divider as Divider2, Typography as Typography10 } from "@wbk/ui";
import { twMerge as twMerge6 } from "tailwind-merge";
import { jsx as jsx13, jsxs as jsxs10 } from "react/jsx-runtime";
var StepsTimeline = ({ section, className }) => {
  var _a;
  const cards = (_a = section.contentCollection) == null ? void 0 : _a.items;
  if (!cards.length) return null;
  return /* @__PURE__ */ jsx13(
    DecoratedSection_default,
    {
      section,
      className: twMerge6("container", className),
      "data-testid": `section_steps_timeline_${section.id}`,
      children: /* @__PURE__ */ jsxs10("div", { className: "space-y-8", children: [
        /* @__PURE__ */ jsxs10("div", { className: "space-y-6", children: [
          /* @__PURE__ */ jsxs10("div", { className: "space-y-2", children: [
            /* @__PURE__ */ jsx13(Typography10, { variant: "heading-S", color: "primary", children: section.subtitle }),
            /* @__PURE__ */ jsx13(Typography10, { variant: "display-S", children: section.title })
          ] }),
          !!section.collapsableDescription && /* @__PURE__ */ jsx13("div", { className: "max-w-2xl", children: /* @__PURE__ */ jsx13(ContentfulRichText_default, { description: section.collapsableDescription }) })
        ] }),
        /* @__PURE__ */ jsx13("div", { className: "flex items-start overflow-x-auto", children: cards.map((card, index) => /* @__PURE__ */ jsxs10(Fragment5, { children: [
          index > 0 && /* @__PURE__ */ jsx13(Divider2, { className: "mt-8 min-w-4 flex-1" }),
          /* @__PURE__ */ jsxs10("div", { className: "flex min-w-16 shrink-0 flex-col items-center space-y-4", children: [
            /* @__PURE__ */ jsxs10("div", { className: "relative flex h-16 w-full items-center justify-center", children: [
              index > 0 && /* @__PURE__ */ jsx13(Divider2, { className: "absolute top-1/2 w-[calc(50%-2rem)] ltr:left-0 rtl:right-0" }),
              /* @__PURE__ */ jsx13("div", { className: "bg-body-level-1 border-paper-level-2 flex h-16 w-16 shrink-0 items-center justify-center rounded-full border", children: /* @__PURE__ */ jsx13(Typography10, { variant: "heading-M", color: "primary", children: index + 1 }) }),
              index < cards.length - 1 && /* @__PURE__ */ jsx13(Divider2, { className: "absolute top-1/2 w-[calc(50%-2rem)] ltr:right-0 rtl:left-0" })
            ] }),
            /* @__PURE__ */ jsxs10("div", { className: "space-y-2 text-center", children: [
              /* @__PURE__ */ jsx13(Typography10, { variant: "heading-S", children: card.title }),
              /* @__PURE__ */ jsx13(Typography10, { color: "text-secondary", children: card.subtitle })
            ] })
          ] })
        ] }, card.id)) })
      ] })
    }
  );
};
var StepsTimeline_default = StepsTimeline;

// src/components/section/TagsImageSection.tsx
import { Typography as Typography11 } from "@wbk/ui";
import { twMerge as twMerge7 } from "tailwind-merge";
import { jsx as jsx14, jsxs as jsxs11 } from "react/jsx-runtime";
var TagsImageSection = ({ section, className }) => {
  var _a;
  const cards = (_a = section.contentCollection) == null ? void 0 : _a.items;
  const background = section.sectionBackground;
  if (!cards.length) return null;
  return /* @__PURE__ */ jsxs11(
    DecoratedSection_default,
    {
      section: __spreadProps(__spreadValues({}, section), { sectionBackground: void 0 }),
      className: twMerge7("container", className),
      "data-testid": `section_tags_image_section_${section.id}`,
      children: [
        !!background && /* @__PURE__ */ jsxs11("div", { className: "pointer-events-none absolute inset-y-0 -z-10 w-full md:w-1/2 ltr:right-0 rtl:left-0", children: [
          /* @__PURE__ */ jsx14(ContentfulMedia_default, { media: background, className: "h-full w-full object-cover" }),
          /* @__PURE__ */ jsx14("div", { className: "from-body to-body absolute inset-0 bg-gradient-to-b via-transparent via-40%" }),
          /* @__PURE__ */ jsx14("div", { className: "from-body absolute inset-0 to-transparent to-60% ltr:bg-gradient-to-r rtl:bg-gradient-to-l" })
        ] }),
        /* @__PURE__ */ jsxs11("div", { className: "space-y-8", children: [
          /* @__PURE__ */ jsxs11("div", { className: "space-y-6", children: [
            /* @__PURE__ */ jsxs11("div", { className: "space-y-2", children: [
              /* @__PURE__ */ jsx14(Typography11, { variant: "heading-S", color: "primary", children: section.subtitle }),
              /* @__PURE__ */ jsx14(Typography11, { variant: "display-S", children: section.title })
            ] }),
            !!section.collapsableDescription && /* @__PURE__ */ jsx14("div", { className: "max-w-2xl", children: /* @__PURE__ */ jsx14(ContentfulRichText_default, { description: section.collapsableDescription }) })
          ] }),
          /* @__PURE__ */ jsx14("div", { className: "flex max-w-xl flex-wrap gap-4", children: cards.map((card) => /* @__PURE__ */ jsxs11(
            "div",
            {
              className: "bg-body-level-1 flex min-w-[123px] items-center justify-center gap-1 rounded-full p-1.5",
              children: [
                !!card.image && /* @__PURE__ */ jsx14(ContentfulMedia_default, { media: card.image, className: "h-4 w-4 shrink-0 rounded" }),
                /* @__PURE__ */ jsx14(Typography11, { color: "text-secondary", strong: true, children: card.title })
              ]
            },
            card.id
          )) })
        ] })
      ]
    }
  );
};
var TagsImageSection_default = TagsImageSection;

export {
  FeaturesGrid_default,
  HtmlSection_default,
  IconsGrid_default,
  IconsLargeGrid_default,
  InfoCardsGrid_default,
  InfoModal_default,
  InfoModalAsync_default,
  RecapSection_default,
  SectionTagFilter_default,
  RectangularCarousel_default,
  RectangularCardCarousel_default,
  ShowcaseCarousel_default,
  StepsTimeline_default,
  TagsImageSection_default
};
//# sourceMappingURL=chunk-XEEAPD2W.mjs.map